/**
 * Loading placeholder shaped like the content it stands for (DESIGN → Motion: skeletons, never
 * a spinner for a screen or a list). Size and shape come from `className`.
 */
export function Skeleton({ className = '' }: { className?: string }) {
	return <div aria-hidden="true" className={`bg-border animate-pulse rounded-full ${className}`} />;
}

/** Rows like list items: an avatar and two lines (chats, applications, notifications…). */
function ListRows({ rows }: { rows: number }) {
	return (
		<ul className="flex flex-col gap-3">
			{Array.from({ length: rows }, (_, n) => (
				<li key={n} className="flex items-center gap-3">
					<Skeleton className="size-12 shrink-0" />
					<div className="flex flex-1 flex-col gap-2">
						<Skeleton className="h-4 w-2/3" />
						<Skeleton className="h-3 w-1/2" />
					</div>
				</li>
			))}
		</ul>
	);
}

/**
 * A whole loading screen (T-1311): `list` for lists, `profile` for S12–S14, `detail` for an
 * event or a form. Keeps `aria-busy` for assistive tech and tests.
 */
export function ScreenSkeleton({
	kind,
	rows = 5,
	bare = false,
}: {
	kind: 'list' | 'profile' | 'detail';
	rows?: number;
	/** Inside an already padded box (a sheet, the chat tab): no own padding. */
	bare?: boolean;
}) {
	return (
		<div aria-busy="true" className={`flex flex-col gap-4 ${bare ? '' : 'px-4 pt-4 pb-6'}`}>
			{kind === 'list' && <ListRows rows={rows} />}
			{kind === 'profile' && (
				<>
					<div className="flex flex-col items-center gap-3">
						<Skeleton className="size-24" />
						<Skeleton className="h-7 w-1/2" />
						<Skeleton className="h-4 w-1/3" />
					</div>
					<Skeleton className="rounded-card h-16 w-full" />
					<div className="flex flex-wrap gap-2">
						{['w-16', 'w-20', 'w-14', 'w-24'].map((width) => (
							<Skeleton key={width} className={`h-8 ${width}`} />
						))}
					</div>
				</>
			)}
			{kind === 'detail' && (
				<>
					<Skeleton className="rounded-card h-40 w-full" />
					<Skeleton className="h-7 w-3/4" />
					<Skeleton className="h-4 w-1/2" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-5/6" />
				</>
			)}
		</div>
	);
}
